import { useEffect, useState, useRef } from "react";
import { Plus, Search, Users as UsersIcon, Camera, Upload } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { GlassCard, PageHeader, EmptyState } from "../components/shared";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const ROLES = ["founder", "lead", "employee", "hr", "manager", "intern"];

const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
        const date = new Date(dateStr);
        const day = date.getDate();
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    } catch (e) {
        return dateStr;
    }
};

const formatSalary = (salary) => {
    if (salary === undefined || salary === null) return "₹0";
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0
    }).format(salary);
};

const getAvatarBg = (name) => {
    const colors = [
        "bg-teal-600",
        "bg-blue-600",
        "bg-pink-600",
        "bg-emerald-600",
        "bg-violet-600",
        "bg-amber-600",
        "bg-rose-600",
        "bg-cyan-600"
    ];
    let hash = 0;
    const n = String(name || "");
    for (let i = 0; i < n.length; i++) {
        hash = n.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
};

const getInitials = (name) => {
    if (!name) return "EE";
    const parts = String(name).trim().split(/\s+/);
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return String(name).substring(0, 2).toUpperCase();
};

const renderRoleBadge = (role) => {
    const r = String(role || "employee").toLowerCase();
    if (r === "lead") {
        return (
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30 dark:text-emerald-400 border border-emerald-200/50 dark:border-emerald-800/30">lead</span>
        );
    } else if (r === "founder") {
        return (
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-teal-50 text-teal-600 dark:bg-teal-950/30 dark:text-teal-400 border border-teal-200/50 dark:border-teal-800/30">founder</span>
        );
    } else {
        return (
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400">{r}</span>
        );
    }
};

export default function Employees() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const canManage = ["super_admin", "hr", "founder"].includes(user?.role);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/employees?q=${encodeURIComponent(q)}`);
            setItems(data.items || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSearch = (e) => {
        e.preventDefault();
        load();
    };

    const removeEmployee = async (id) => {
        if (!window.confirm("Are you sure you want to delete this employee? This will permanently delete their account and attendance data.")) return;
        try {
            await api.delete(`/employees/${id}`);
            toast.success("Employee permanently deleted");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const downloadIdCardForEmployee = async (employeeId, employeeName) => {
        toast.loading("Generating ID Card...", { id: "id-download" });
        try {
            const response = await api.get(`/documents/id-card/${employeeId}`, { responseType: 'blob' });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `id_card_${employeeName.replace(/\s+/g, '_')}.pdf`;
            link.click();
            toast.success("Downloaded ID Card PDF", { id: "id-download" });
        } catch (e) {
            toast.error("Failed to download ID Card", { id: "id-download" });
        }
    };

    return (
        <div>
            <PageHeader
                title="Employees"
                subtitle="Manage people, roles and access permissions across your organization."
                testId="employees-header"
                actions={
                    canManage && (
                        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                            <DialogTrigger asChild>
                                <Button className="rounded-full bg-teal-600 hover:bg-teal-700 text-white" data-testid="add-employee-btn">
                                    <Plus className="w-4 h-4 mr-1" /> Add employee
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
                                <DialogHeader>
                                    <DialogTitle>Add employee</DialogTitle>
                                </DialogHeader>
                                <CreateEmployeeForm items={items} onCreated={() => { setCreateOpen(false); load(); }} />
                            </DialogContent>
                        </Dialog>
                    )
                }
            />

            <GlassCard className="p-4 mb-6">
                <form onSubmit={onSearch} className="flex gap-2">
                    <div className="flex-1 flex items-center gap-2 px-3 rounded-xl bg-black/[0.03] dark:bg-white/[0.03]">
                        <Search className="w-4 h-4 text-muted-foreground" />
                        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people…" className="border-none bg-transparent focus-visible:ring-0 px-0" data-testid="employee-search" />
                    </div>
                    <Button type="submit" variant="outline" className="rounded-full border-gray-200 hover:bg-gray-50 bg-white text-black">Search</Button>
                </form>
            </GlassCard>

            <GlassCard className="overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
                ) : items.length === 0 ? (
                    <EmptyState icon={UsersIcon} title="No employees yet" description="Add your first team member to get started." />
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-black/[0.01] dark:bg-white/[0.01] hover:bg-black/[0.01]">
                                    <TableHead className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">EMPLOYEE</TableHead>
                                    <TableHead className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">ROLE</TableHead>
                                    <TableHead className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">DEPARTMENT</TableHead>
                                    <TableHead className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">JOINED</TableHead>
                                    <TableHead className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">SALARY</TableHead>
                                    <TableHead className="text-[11px] uppercase tracking-wider font-semibold text-gray-500 text-right">ACTIONS</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {items.map((e) => (
                                    <TableRow key={e.id} className="hover:bg-black/[0.01] dark:hover:bg-white/[0.01] border-b" data-testid={`employee-row-${e.id}`}>
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                {e.avatar ? (
                                                    <img src={e.avatar} alt={e.name} className="w-9 h-9 rounded-full object-cover" />
                                                ) : (
                                                    <div className={`w-9 h-9 rounded-full ${getAvatarBg(e.name)} flex items-center justify-center text-white font-semibold text-sm`}>
                                                        {getInitials(e.name)}
                                                    </div>
                                                )}
                                                <div>
                                                    <Link to={`/employees/${e.id}`} className="font-semibold text-gray-900 dark:text-gray-100 hover:text-teal-600 transition">{e.name}</Link>
                                                    <div className="text-[11px] text-muted-foreground">{e.employee_id} - {e.email}</div>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className="align-middle">{renderRoleBadge(e.role)}</TableCell>
                                        <TableCell className="text-sm font-medium text-gray-600 dark:text-gray-300 align-middle">{e.department || "—"}</TableCell>
                                        <TableCell className="text-sm font-medium text-gray-600 dark:text-gray-300 align-middle">{formatDate(e.joining_date)}</TableCell>
                                        <TableCell className="text-sm font-medium text-gray-600 dark:text-gray-300 align-middle">{formatSalary(e.salary)}</TableCell>
                                        <TableCell className="text-right align-middle">
                                            {canManage && (
                                                <div className="flex justify-end gap-2">
                                                    <Button size="sm" variant="outline" onClick={() => downloadIdCardForEmployee(e.id, e.name)} className="rounded-lg text-xs bg-white text-black border border-gray-200 hover:bg-gray-50 h-8 font-medium">QR PDF</Button>
                                                    <Button size="sm" variant="outline" onClick={() => setEditing(e)} className="rounded-lg text-xs bg-white text-black border border-gray-200 hover:bg-gray-50 h-8 font-medium">Edit</Button>
                                                    <Button size="sm" onClick={() => removeEmployee(e.id)} className="rounded-lg text-xs bg-red-600 hover:bg-red-700 text-white h-8 font-medium">Delete</Button>
                                                </div>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </GlassCard>

            <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
                <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Edit employee</DialogTitle>
                    </DialogHeader>
                    {editing && <EditEmployeeForm employee={editing} items={items} onSaved={() => { setEditing(null); load(); }} />}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function CreateEmployeeForm({ items, onCreated }) {
    const [form, setForm] = useState({
        name: "",
        employee_id: "",
        email: "",
        password: "welcome123",
        role: "employee",
        designation: "",
        department: "Engineering",
        reporting_manager: "",
        joining_date: new Date().toISOString().substring(0, 10),
        salary: 0,
        phone: "",
        location: "",
        avatar: ""
    });
    const [loading, setLoading] = useState(false);
    const [cameraActive, setCameraActive] = useState(false);
    
    const fileInputRef = useRef(null);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/employees", {
                ...form,
                salary: Number(form.salary)
            });
            toast.success("Employee added successfully!");
            onCreated();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    const generateEmployeeId = () => {
        const prefix = form.role === "lead" ? "PWC-TL-" : (form.role === "founder" ? "PWC-FND-" : "PWC-EMP-");
        const nums = items
            .filter(item => item.employee_id && item.employee_id.startsWith(prefix))
            .map(item => {
                const part = item.employee_id.substring(prefix.length);
                const num = parseInt(part, 10);
                return isNaN(num) ? 0 : num;
            });
        const nextNum = nums.length > 0 ? Math.max(...nums) + 1 : (form.role === "lead" ? 1 : 2); // Start EMP at 002 if no others, TL at 001
        const padded = String(nextNum).padStart(3, '0');
        setForm(prev => ({ ...prev, employee_id: `${prefix}${padded}` }));
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setForm(prev => ({ ...prev, avatar: reader.result }));
            };
            reader.readAsDataURL(file);
        }
    };

    const startCamera = async () => {
        setCameraActive(true);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }
        } catch (e) {
            toast.error("Could not access camera");
            setCameraActive(false);
        }
    };

    const capturePhoto = () => {
        if (videoRef.current && canvasRef.current) {
            const video = videoRef.current;
            const canvas = canvasRef.current;
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const dataUrl = canvas.toDataURL('image/jpeg');
            setForm(prev => ({ ...prev, avatar: dataUrl }));
            stopCamera();
        }
    };

    const stopCamera = () => {
        if (videoRef.current && videoRef.current.srcObject) {
            const stream = videoRef.current.srcObject;
            stream.getTracks().forEach(track => track.stop());
            videoRef.current.srcObject = null;
        }
        setCameraActive(false);
    };

    // Clean up webcam stream on unmount
    useEffect(() => {
        const currentVideo = videoRef.current;
        return () => {
            if (currentVideo && currentVideo.srcObject) {
                const stream = currentVideo.srcObject;
                stream.getTracks().forEach(track => track.stop());
            }
        };
    }, []);

    // Filter potential leads (employees with lead/founder roles)
    const leads = items.filter(i => ["lead", "founder", "manager", "super_admin"].includes(i.role));

    return (
        <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <Label className="text-xs font-semibold text-gray-700">Name</Label>
                    <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="emp-form-name" />
                </div>
                <div>
                    <Label className="text-xs font-semibold text-gray-700">Employee ID</Label>
                    <div className="flex gap-2 mt-1">
                        <Input required value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} className="flex-1 font-mono" />
                        <Button type="button" onClick={generateEmployeeId} variant="outline" className="bg-white border text-black hover:bg-gray-50">Generate</Button>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <Label className="text-xs font-semibold text-gray-700">Email</Label>
                    <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="emp-form-email" />
                </div>
                <div>
                    <Label className="text-xs font-semibold text-gray-700">Password</Label>
                    <Input required type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" data-testid="emp-form-password" />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <Label className="text-xs font-semibold text-gray-700">Role</Label>
                    <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1 w-full h-10 rounded-xl border border-input bg-background px-3 text-sm" data-testid="emp-form-role">
                        {ROLES.map((r) => <option key={r} value={r}>{r.toUpperCase()}</option>)}
                    </select>
                </div>
                <div>
                    <Label className="text-xs font-semibold text-gray-700">Position</Label>
                    <Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} className="mt-1" data-testid="emp-form-designation" />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <Label className="text-xs font-semibold text-gray-700">Department</Label>
                    <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="mt-1" data-testid="emp-form-dept" />
                </div>
                <div>
                    <Label className="text-xs font-semibold text-gray-700">Team lead</Label>
                    <select value={form.reporting_manager} onChange={(e) => setForm({ ...form, reporting_manager: e.target.value })} className="mt-1 w-full h-10 rounded-xl border border-input bg-background px-3 text-sm">
                        <option value="">Unassigned</option>
                        {leads.map((l) => <option key={l.id} value={l.name}>{l.name}</option>)}
                    </select>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <Label className="text-xs font-semibold text-gray-700">Joined</Label>
                    <Input type="date" value={form.joining_date} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} className="mt-1" />
                </div>
                <div>
                    <Label className="text-xs font-semibold text-gray-700">Monthly salary</Label>
                    <Input type="number" value={form.salary} onChange={(e) => setForm({ ...form, salary: Number(e.target.value) })} className="mt-1" />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <Label className="text-xs font-semibold text-gray-700">Phone</Label>
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" />
                </div>
                <div>
                    <Label className="text-xs font-semibold text-gray-700">Location</Label>
                    <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1" />
                </div>
            </div>

            <div>
                <Label className="text-xs font-semibold text-gray-700">Profile photo</Label>
                <div className="flex items-center gap-4 mt-2">
                    <div className="w-16 h-16 rounded-full overflow-hidden bg-teal-600 flex items-center justify-center text-white text-xl font-bold border shadow-inner">
                        {form.avatar ? (
                            <img src={form.avatar} alt="Avatar Preview" className="w-full h-full object-cover" />
                        ) : (
                            form.name ? getInitials(form.name) : "UP"
                        )}
                    </div>
                    <div className="flex gap-2">
                        <Button type="button" size="sm" variant="outline" className="bg-white border text-black hover:bg-gray-50 flex items-center gap-1.5" onClick={() => fileInputRef.current?.click()}>
                            <Upload className="w-3.5 h-3.5" /> Upload
                        </Button>
                        <Button type="button" size="sm" variant="outline" className="bg-white border text-black hover:bg-gray-50 flex items-center gap-1.5" onClick={cameraActive ? capturePhoto : startCamera}>
                            <Camera className="w-3.5 h-3.5" /> {cameraActive ? "Capture" : "Camera"}
                        </Button>
                        {cameraActive && (
                            <Button type="button" size="sm" variant="destructive" onClick={stopCamera} className="h-8">Cancel</Button>
                        )}
                        <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileUpload} />
                    </div>
                </div>
                {cameraActive && (
                    <div className="mt-3 relative rounded-lg overflow-hidden bg-black aspect-video w-full max-w-[320px] mx-auto border shadow-md">
                        <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" />
                        <canvas ref={canvasRef} className="hidden" />
                    </div>
                )}
            </div>

            <Button type="submit" className="w-full rounded-xl bg-teal-600 hover:bg-teal-700 text-white h-11 font-semibold" disabled={loading} data-testid="emp-form-submit">
                {loading ? "Saving…" : "Save"}
            </Button>
        </form>
    );
}

function EditEmployeeForm({ employee, items, onSaved }) {
    const [form, setForm] = useState({
        name: employee.name || "",
        employee_id: employee.employee_id || "",
        email: employee.email || "",
        role: employee.role || "employee",
        designation: employee.designation || "",
        department: employee.department || "",
        reporting_manager: employee.reporting_manager || "",
        joining_date: employee.joining_date || "",
        salary: employee.salary || 0,
        phone: employee.phone || "",
        location: employee.location || "",
        avatar: employee.avatar || "",
        status: employee.status || "active"
    });
    const [loading, setLoading] = useState(false);
    const [cameraActive, setCameraActive] = useState(false);
    
    const fileInputRef = useRef(null);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.patch(`/employees/${employee.id}`, {
                ...form,
                salary: Number(form.salary)
            });
            toast.success("Employee updated successfully");
            onSaved();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setForm(prev => ({ ...prev, avatar: reader.result }));
            };
            reader.readAsDataURL(file);
        }
    };

    const startCamera = async () => {
        setCameraActive(true);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }
        } catch (e) {
            toast.error("Could not access camera");
            setCameraActive(false);
        }
    };

    const capturePhoto = () => {
        if (videoRef.current && canvasRef.current) {
            const video = videoRef.current;
            const canvas = canvasRef.current;
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const dataUrl = canvas.toDataURL('image/jpeg');
            setForm(prev => ({ ...prev, avatar: dataUrl }));
            stopCamera();
        }
    };

    const stopCamera = () => {
        if (videoRef.current && videoRef.current.srcObject) {
            const stream = videoRef.current.srcObject;
            stream.getTracks().forEach(track => track.stop());
            videoRef.current.srcObject = null;
        }
        setCameraActive(false);
    };

    useEffect(() => {
        const currentVideo = videoRef.current;
        return () => {
            if (currentVideo && currentVideo.srcObject) {
                const stream = currentVideo.srcObject;
                stream.getTracks().forEach(track => track.stop());
            }
        };
    }, []);

    const leads = items.filter(i => ["lead", "founder", "manager", "super_admin"].includes(i.role) && i.id !== employee.id);

    return (
        <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <Label className="text-xs font-semibold text-gray-700">Name</Label>
                    <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" />
                </div>
                <div>
                    <Label className="text-xs font-semibold text-gray-700">Employee ID</Label>
                    <Input required value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} className="mt-1 font-mono" />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <Label className="text-xs font-semibold text-gray-700">Email</Label>
                    <Input required type="email" value={form.email} readOnly className="mt-1 bg-gray-50 cursor-not-allowed" />
                </div>
                <div>
                    <Label className="text-xs font-semibold text-gray-700">Status</Label>
                    <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="mt-1 w-full h-10 rounded-xl border border-input bg-background px-3 text-sm">
                        {["active", "on_leave", "exited", "archived"].map((s) => <option key={s} value={s}>{s.replace("_", " ").toUpperCase()}</option>)}
                    </select>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <Label className="text-xs font-semibold text-gray-700">Role</Label>
                    <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1 w-full h-10 rounded-xl border border-input bg-background px-3 text-sm">
                        {ROLES.map((r) => <option key={r} value={r}>{r.toUpperCase()}</option>)}
                    </select>
                </div>
                <div>
                    <Label className="text-xs font-semibold text-gray-700">Position</Label>
                    <Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} className="mt-1" />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <Label className="text-xs font-semibold text-gray-700">Department</Label>
                    <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="mt-1" />
                </div>
                <div>
                    <Label className="text-xs font-semibold text-gray-700">Team lead</Label>
                    <select value={form.reporting_manager} onChange={(e) => setForm({ ...form, reporting_manager: e.target.value })} className="mt-1 w-full h-10 rounded-xl border border-input bg-background px-3 text-sm">
                        <option value="">Unassigned</option>
                        {leads.map((l) => <option key={l.id} value={l.name}>{l.name}</option>)}
                    </select>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <Label className="text-xs font-semibold text-gray-700">Joined</Label>
                    <Input type="date" value={form.joining_date} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} className="mt-1" />
                </div>
                <div>
                    <Label className="text-xs font-semibold text-gray-700">Monthly salary</Label>
                    <Input type="number" value={form.salary} onChange={(e) => setForm({ ...form, salary: Number(e.target.value) })} className="mt-1" />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <Label className="text-xs font-semibold text-gray-700">Phone</Label>
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" />
                </div>
                <div>
                    <Label className="text-xs font-semibold text-gray-700">Location</Label>
                    <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1" />
                </div>
            </div>

            <div>
                <Label className="text-xs font-semibold text-gray-700">Profile photo</Label>
                <div className="flex items-center gap-4 mt-2">
                    <div className="w-16 h-16 rounded-full overflow-hidden bg-teal-600 flex items-center justify-center text-white text-xl font-bold border shadow-inner">
                        {form.avatar ? (
                            <img src={form.avatar} alt="Avatar Preview" className="w-full h-full object-cover" />
                        ) : (
                            form.name ? (form.name.trim().split(/\s+/).length >= 2 ? (form.name.trim().split(/\s+/)[0][0] + form.name.trim().split(/\s+/)[1][0]).toUpperCase() : form.name.substring(0,2).toUpperCase()) : "UP"
                        )}
                    </div>
                    <div className="flex gap-2">
                        <Button type="button" size="sm" variant="outline" className="bg-white border text-black hover:bg-gray-50 flex items-center gap-1.5" onClick={() => fileInputRef.current?.click()}>
                            <Upload className="w-3.5 h-3.5" /> Upload
                        </Button>
                        <Button type="button" size="sm" variant="outline" className="bg-white border text-black hover:bg-gray-50 flex items-center gap-1.5" onClick={cameraActive ? capturePhoto : startCamera}>
                            <Camera className="w-3.5 h-3.5" /> {cameraActive ? "Capture" : "Camera"}
                        </Button>
                        {cameraActive && (
                            <Button type="button" size="sm" variant="destructive" onClick={stopCamera} className="h-8">Cancel</Button>
                        )}
                        <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileUpload} />
                    </div>
                </div>
                {cameraActive && (
                    <div className="mt-3 relative rounded-lg overflow-hidden bg-black aspect-video w-full max-w-[320px] mx-auto border shadow-md">
                        <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" />
                        <canvas ref={canvasRef} className="hidden" />
                    </div>
                )}
            </div>

            <Button type="submit" className="w-full rounded-xl bg-teal-600 hover:bg-teal-700 text-white h-11 font-semibold" disabled={loading}>
                {loading ? "Saving…" : "Save Changes"}
            </Button>
        </form>
    );
}
